import { IRunOrderClass, IDogRunOrderEntry, IScore, IShow, IEvent, isLeftoverRunningOrderClass } from "@eagerdog/interfaces";
import {
  Constants,
  akcConformationClassMatches,
  compareTimeSlots,
  isPerClassTimeslotShow,
  isUKCConformationShow,
  isUKCConformationUnscheduledLevel,
  countsTowardUKCBreedTile,
} from "@eagerdog/constants";
import { calculateUkcJumpHeights, calculateUkcRallyJumpHeight } from "./ukcJumpHeightCalculator";
import { processUkcAgilityRunOrder } from "../routes/Event/ManageShow/RunOrderTab/utils/ukcAgilityRunOrder";

/**
 * Process AKC Fast CAT run order by grouping by timeslots instead of judges
 */
function processAkcFastCatRunOrder(selectedShow: IShow, selectedRing: string, allScores: IScore[]): { processedClasses: IRunOrderClass[], totalEntries: number, completedEntries: number } {
  
  const processedClasses: IRunOrderClass[] = [];
  let totalEntries = 0;
  let completedEntries = 0;
  
  // Filter scores to only include those for the selected show
  const selectedShowIdStr = selectedShow._id?.toString();
  const showScores = allScores.filter(score => {
    const scoreShowId = (score.show_id as any)?._id || score.show_id;
    const scoreShowIdStr = scoreShowId?.toString();
    return scoreShowIdStr === selectedShowIdStr;
  });
  
  // Group scores by timeslot
  const timeslotClassMap = new Map<string, Map<string, IScore[]>>();
  
  showScores.forEach(score => {
    const timeslot = score.time_slot || 'No Timeslot';
    const groupingKey = score.dog_class?.level || 'Unknown Level';
    
    if (!timeslotClassMap.has(timeslot)) {
      timeslotClassMap.set(timeslot, new Map());
    }
    
    const timeslotClasses = timeslotClassMap.get(timeslot)!;
    if (!timeslotClasses.has(groupingKey)) {
      timeslotClasses.set(groupingKey, []);
    }
    
    timeslotClasses.get(groupingKey)!.push(score);
  });
  
  // Create run order classes from timeslot-organized scores
  timeslotClassMap.forEach((classes, timeslot) => {
    classes.forEach((scores, classLevel) => {
      const dogs = scores.map(score => ({
        scoreId: score._id,
        armband: score.arm_number || '',
        dogName: score.call_name || '',
        handlerName: score.owner_name || '',
        breedName: score.breed || '',
        originalLevel: classLevel,
        agentName: score.agent_name || ''
      }));
      
      // Check if there's an existing running_order for this timeslot/class and apply it
      const ring = selectedShow.rings?.find(r => r.ring === selectedRing);
      if (ring?.running_order) {
        const runOrderEntry = ring.running_order.find(ro => ro.judge_name === timeslot);
        if (runOrderEntry) {
          const dogClass = runOrderEntry.dog_classes?.find(dc => dc.level === classLevel && dc.time_slot === timeslot);
          if (dogClass?.dog_run_order && dogClass.dog_run_order.length > 0) {
            // Sort dogs according to the saved dog_run_order
            dogs.sort((a, b) => {
              const aIndex = dogClass.dog_run_order!.indexOf(a.scoreId);
              const bIndex = dogClass.dog_run_order!.indexOf(b.scoreId);
              // If not found in order, put at end
              if (aIndex === -1) return 1;
              if (bIndex === -1) return -1;
              return aIndex - bIndex;
            });
          }
        }
      }
      
      totalEntries += dogs.length;
      // For Fast CAT, we don't track completed dogs the same way
      
      processedClasses.push({
        id: `${timeslot}-${classLevel}`,
        showElement: scores[0]?.dog_class?.show_element || 'Fast CAT',
        level: classLevel,
        judgeName: timeslot, // Use timeslot as the "judge" name for grouping
        dogs: dogs,
        originalDogClass: scores[0]?.dog_class || null,
        completedDogs: []
      });
    });
  });
  
  
  return {
    processedClasses,
    totalEntries,
    completedEntries
  };
}

export interface IProcessRunOrderDataParams {
  selectedShow: IShow;
  selectedRing: string;
  allScores: IScore[];
  event?: IEvent; // Optional for Fast CAT timeslot grouping and sanctioning club
}

export interface IProcessedRunOrderData {
  processedClasses: IRunOrderClass[];
  totalEntries: number;
  completedEntries: number;
}

const isPreferredObedienceLevel = (level?: string): boolean => {
  if (!level) return false;
  return [
    Constants.dog_class_element_level.preferred_novice,
    Constants.dog_class_element_level.preferred_open,
    Constants.dog_class_element_level.preferred_utility,
  ].includes(level);
};

const compareByJumpThenArmband = (a: IDogRunOrderEntry, b: IDogRunOrderEntry): number => {
  if (a.jumpHeight !== undefined && b.jumpHeight !== undefined && a.jumpHeight !== b.jumpHeight) {
    return a.jumpHeight - b.jumpHeight;
  }

  const aNum = parseInt((a.armband || "0").replace(/[^0-9]/g, ""), 10) || 0;
  const bNum = parseInt((b.armband || "0").replace(/[^0-9]/g, ""), 10) || 0;
  if (aNum !== bNum) return aNum - bNum;

  const aHasP = (a.armband || "").startsWith("P");
  const bHasP = (b.armband || "").startsWith("P");
  if (!aHasP && bHasP) return -1;
  if (aHasP && !bHasP) return 1;
  return 0;
};

/**
 * Default order for shows where each dog picks its own time slot (General Obedience/Rally
 * Show N Go): chronological by time slot, then the usual jump-height/armband order within a slot.
 */
export const compareByTimeSlotThenArmband = (a: IDogRunOrderEntry, b: IDogRunOrderEntry): number => {
  const slotCompare = compareTimeSlots(a.timeSlot, b.timeSlot);
  if (slotCompare !== 0) return slotCompare;
  return compareByJumpThenArmband(a, b);
};

/**
 * Shared utility to process run order data consistently between admin RunOrderTab and RealtimeRunOrderTab
 * This ensures both tabs display exactly the same data structure and counts
 */
export function processRunOrderData(params: IProcessRunOrderDataParams): IProcessedRunOrderData {
  const { selectedShow, selectedRing, allScores, event } = params;
  
  // Check if this is AKC Fast CAT - use timeslot grouping instead of running_order
  const isAkcFastCat = event?.sanctioning_club === 'AKC' && selectedShow.show_type === 'Fast CAT';
  
  if (isAkcFastCat) {
    return processAkcFastCatRunOrder(selectedShow, selectedRing, allScores);
  }

  // Check if this is UKC Agility - use predefined level grouping
  const isUkcAgility = event?.sanctioning_club === 'UKC' && selectedShow.show_type === Constants.show_type.agility;

  // AKC Conformation: match tiles to scores on the normalized class element so a tile stored as
  // "Conformation | Veteran Sweepstakes (7-9 years)" still finds scores stored under "Non-Licensed Classes".
  const isAkcConformation =
    event?.sanctioning_club === Constants.sanctioning_club.AKC &&
    selectedShow.show_type === Constants.show_type.conformation;
  // UKC Conformation: a Conformation tile is a breed group (optionally an A–Z slice)
  // or a single breed with its own judge, so match scores by breed, not class level.
  const isUkcConformation = isUKCConformationShow(event?.sanctioning_club, selectedShow.show_type);
  const breedsWithOwnJudge = new Set((selectedShow.breed_judges || []).map((bj) => bj.breed));
  const isUkcBreedTile = (dc: { show_element?: string }): boolean =>
    isUkcConformation && dc.show_element === Constants.dog_class_element.conformation;
  const ukcBreedTileMatches = (
    score: IScore,
    dc: { start_letter?: string; end_letter?: string },
    levelsToSearch: string[]
  ): boolean => {
    if (!countsTowardUKCBreedTile(score.dog_class)) return false;
    return levelsToSearch.some((level) => {
      if (!(Constants.ukc_breed_group_order as string[]).includes(level)) {
        return score.breed === level;
      }
      if (score.breed_group !== level || breedsWithOwnJudge.has(score.breed || "")) return false;
      if (!dc.start_letter || !dc.end_letter) return true;
      const first = (score.breed || "")[0]?.toUpperCase() ?? "A";
      return first >= dc.start_letter && first <= dc.end_letter;
    });
  };
  const classElementMatches = (score: IScore, dc: { show_element?: string; level?: string; start_letter?: string; end_letter?: string }, levelsToSearch: string[]): boolean => {
    if (isUkcBreedTile(dc)) {
      return ukcBreedTileMatches(score, dc, levelsToSearch);
    }
    if (isAkcConformation) {
      return levelsToSearch.some((level) =>
        akcConformationClassMatches({ show_element: dc.show_element, level }, score.dog_class || {})
      );
    }
    return score.dog_class?.show_element === dc.show_element && levelsToSearch.includes(score.dog_class?.level as string);
  };
  
  if (isUkcAgility) {
    return processUkcAgilityRunOrder(selectedShow, selectedRing, allScores);
  }

  // General Obedience/Rally Show N Go: dogs pick a time slot per class, so the class structure
  // still follows the judge schedule but dogs run in time-slot order (not armband order).
  const isTimeslotOrdered = isPerClassTimeslotShow(event?.sanctioning_club, selectedShow.show_type);
  const defaultDogCompare = isTimeslotOrdered ? compareByTimeSlotThenArmband : compareByJumpThenArmband;
  
  const processedClasses: IRunOrderClass[] = [];
  let totalEntries = 0;
  let completedEntries = 0;

  // Get the saved running order from Judge Schedule
  const currentRing = selectedShow.rings?.find(r => r.ring === selectedRing);
  
  if (!currentRing?.running_order) {

    return { processedClasses: [], totalEntries: 0, completedEntries: 0 };
  }

  // Pre-scan: for each logical split group (show_element:level:section), determine
  // whether ANY sibling split tile already has explicit dogs in dog_run_order.
  // This lets us distinguish "dogs not yet distributed" (all empty → show all)
  // from "this split genuinely has 0 dogs" (at least one sibling has dogs → show none).
  const splitGroupHasAssignedDogs = new Map<string, boolean>();
  for (const runOrderItem of currentRing.running_order) {
    if (runOrderItem.dog_classes) {
      for (const dc of runOrderItem.dog_classes) {
        if ((dc as any).split_number !== undefined) {
          const key = `${dc.show_element}:${dc.level}:${(dc as any).section || ''}`;
          if (dc.dog_run_order && dc.dog_run_order.length > 0) {
            splitGroupHasAssignedDogs.set(key, true);
          } else if (!splitGroupHasAssignedDogs.has(key)) {
            splitGroupHasAssignedDogs.set(key, false);
          }
        }
      }
    }
  }

  // Use the processed data from the show (which includes completed_dogs) - EXACT RunOrderTab logic
  for (const runOrderItem of currentRing.running_order) {
    if (runOrderItem.dog_classes) {

      
      for (const dc of runOrderItem.dog_classes) {
        
        // Skip child levels that are grouped into parent levels (EXACT RunOrderTab logic)
        // TEMPORARILY DISABLED: We need to process grouped classes separately to preserve their individual orders
        // if ((dc as any).group_with) {
        //   continue;
        // }

        // Skip grouped child classes - they'll be included in their parent class
        if ((dc as any).group_with) {
          continue;
        }

        if (dc.type === "dog_class" && dc.show_element && dc.level) {
          if (isUkcConformation && isUKCConformationUnscheduledLevel(dc.level)) {
            continue;
          }
          if (
            !isUkcBreedTile(dc) &&
            isLeftoverRunningOrderClass(
              runOrderItem.judge_name,
              dc,
              currentRing.running_order,
              allScores.filter((score) => {
                const scoreShowId = (score.show_id as any)?._id || score.show_id;
                return scoreShowId?.toString() === selectedShow._id?.toString();
              })
            )
          ) {
            continue;
          }

          // Find matching scores for this class and any grouped levels - EXACT RunOrderTab logic
          const dogEntries: IDogRunOrderEntry[] = [];
          
          // For individual class processing, only search for this class's own level
          // This prevents dogs from being loaded into multiple classes
          const levelsToSearch = [dc.level];
          
          // Find all levels in this judge's running order that are grouped with the current level
          // NEW format: "show_element:level" (e.g., "Obedience:Preferred Open")
          // OLD format: simple level name (e.g., "Open B")
          const parentIdentifier = `${dc.show_element}:${dc.level}`;
          const parentLevel = dc.level;
          for (const otherDc of runOrderItem.dog_classes) {
            if (((otherDc as any).group_with === parentIdentifier || (otherDc as any).group_with === parentLevel) && otherDc.level) {
              levelsToSearch.push(otherDc.level);
            }
          }
          
          // DISABLED: Also check if this level has a label with grouped levels (backup method)
          // if ((dc as any).label) {
          //   const groupedLevels = (dc as any).label.match(/\(([^)]+)\)/g);
          //   if (groupedLevels) {
          //     const cleanedLevels = groupedLevels.map((level: string) => level.replace(/[()]/g, ''));
          //     levelsToSearch.push(...cleanedLevels);
          //   }
          // }

          // Check if this class has sex_split (for AKC Conformation specialty shows)
          const sexSplit = (dc as any).sex_split as "Dogs" | "Bitches" | undefined;
          
          // Check if this class has variation (for AKC Conformation - Open, American Bred, BBE)
          const classVariation = (dc as any).variation as string | undefined;
          
          // Debug: Log variation info for classes that should have it
          if (dc.level === Constants.dog_class_element_level.akc_open || 
              dc.level === Constants.dog_class_element_level.akc_american_bred) {
          }
          
          // Map sex_split to dog_sex values
          const filterBySex = sexSplit 
            ? (sexSplit === "Dogs" ? "Male" : "Female")
            : undefined;

          // Process scores for all levels (main + grouped) - EXACT RunOrderTab logic
          const addedScoreIds = new Set<string>(); // Track added scores to prevent duplicates
          for (const score of allScores) {
            // Handle both string ID and ObjectId object for robust comparison
            const scoreShowId = (score.show_id as any)?._id || score.show_id;
            const scoreShowIdStr = scoreShowId?.toString();
            const selectedShowIdStr = selectedShow._id?.toString();

            // If sex_split is set, filter by dog sex
            if (filterBySex && score.dog_sex !== filterBySex) {
              continue;
            }
            
            // If variation is set, filter by variation
            if (classVariation && score.variation !== classVariation) {
              continue;
            }

            if (classElementMatches(score, dc, levelsToSearch) &&
                (scoreShowIdStr && selectedShowIdStr && scoreShowIdStr === selectedShowIdStr)) {
              if ((dc as any).split_number === undefined && !isUkcBreedTile(dc)) {
                const scoreJudge = (score.judge_name || "").trim();
                const tileJudge = (runOrderItem.judge_name || "").trim();
                if (scoreJudge && tileJudge && scoreJudge !== tileJudge) {
                  continue;
                }
              }
              
              // For shows with sections, also match by section
              const dcSection = (dc as any).section;
              if (dcSection && score.dog_class?.section !== dcSection) {
                continue;
              }
              
              // Skip if we've already added this score (prevents duplicates in grouped classes)
              const scoreIdStr = score._id?.toString();
              if (addedScoreIds.has(scoreIdStr)) {
                continue;
              }
              addedScoreIds.add(scoreIdStr);
              
              // Format armband with "P" prefix for preferred classes
              let formattedArmband = score.arm_number || "";
              if (formattedArmband && score.dog_class?.level?.toLowerCase()?.includes("preferred")) {
                formattedArmband = `P${formattedArmband}`;
              }
              
              // Determine jump height based on show type and sanctioning club
              let jumpHeight: number | undefined;
              
              // For Agility, use agility_jump_height field
              if (selectedShow.show_type === Constants.show_type.agility) {
                jumpHeight = score.agility_jump_height;
              } 
              // For Obedience and Rally Obedience
              else if (dc.show_element === Constants.dog_class_element.performance_obedience || 
                  dc.show_element === Constants.dog_class_element.performance_rally_obedience) {
                
                if (event?.sanctioning_club === Constants.sanctioning_club.UKC) {
                  // For UKC, calculate jump height from height_at_withers and jump_height_type.
                  // Rally Obedience uses a 3-tier system (6/10/14 Standard, 4/8/12 Minimum).
                  // Obedience uses the full multi-step map.
                  // If those fields are absent there is no jump height — do not fall back to AKC fields.
                  if (score.height_at_withers && score.jump_height_type) {
                    if (dc.show_element === Constants.dog_class_element.performance_rally_obedience) {
                      jumpHeight = calculateUkcRallyJumpHeight(score.height_at_withers, score.jump_height_type) ?? undefined;
                    } else {
                      const jumpHeights = calculateUkcJumpHeights(score.height_at_withers, score.jump_height_type);
                      jumpHeight = jumpHeights?.jump_height;
                    }
                  }
                } else {
                  // For AKC and other clubs, use the stored jump height field
                  if (dc.show_element === Constants.dog_class_element.performance_obedience) {
                    jumpHeight = isPreferredObedienceLevel(score.dog_class?.level)
                      ? score.akc_obedience_preferred_jump_height ?? score.obedience_jump_height
                      : score.obedience_jump_height;
                  } else if (dc.show_element === Constants.dog_class_element.performance_rally_obedience) {
                    jumpHeight = score.rally_jump_height;
                  }
                }
              }

              dogEntries.push({
                scoreId: score._id,
                armband: formattedArmband,
                dogName: score.call_name || "",
                handlerName: score.owner_name || "",
                breedName: score.breed || "",
                originalLevel: score.dog_class?.level || dc.level, // Store the original individual level for grouped class saving
                section: score.dog_class?.section,
                jumpHeight: jumpHeight,
                agentName: score.agent_name || "",
                timeSlot: score.time_slot || undefined
              });
            }
          }
          
          // For split tiles that have an explicit dog_run_order, filter to only those dogs.
          // Stringify both sides to avoid ObjectId vs string comparison mismatches.
          //
          // Empty dog_run_order on a split tile has two cases:
          //   a) At least one sibling split has dogs → this split genuinely has 0 dogs assigned.
          //      Show nothing to avoid duplicating dogs across splits.
          //   b) No sibling split has any dogs yet (dogs were never explicitly ordered before
          //      the split was created). Deterministically distribute by position using the
          //      same alternating logic as splitDogClass so each split shows a unique subset
          //      without requiring the user to press Auto-distribute first.
          const splitNumber: number | undefined = (dc as any).split_number;
          const splitTotal: number | undefined = (dc as any).split_total;
          if (splitNumber !== undefined) {
            const assignedIds = (dc.dog_run_order ?? []).map((id: any) => id?.toString());
            if (assignedIds.length > 0) {
              // Explicit assignment — keep only assigned dogs
              const assignedSet = new Set<string>(assignedIds);
              const filtered = dogEntries.filter(e => assignedSet.has(e.scoreId?.toString()));
              dogEntries.length = 0;
              dogEntries.push(...filtered);
            } else {
              const key = `${dc.show_element}:${dc.level}:${(dc as any).section || ''}`;
              const anySiblingHasDogs = splitGroupHasAssignedDogs.get(key) === true;
              if (anySiblingHasDogs) {
                // A sibling already has explicit dogs → this split has 0 assigned
                dogEntries.length = 0;
              } else if (splitTotal !== undefined && splitTotal > 1) {
                // No sibling has explicit dogs yet. Deterministically distribute by position
                // (same alternating order as splitDogClass) so dogs aren't duplicated across
                // splits even before the user runs Auto-distribute.
                // Sort by the default order first so the distribution is stable and predictable.
                dogEntries.sort(defaultDogCompare);
                const distributed = dogEntries.filter((_, i) => i % splitTotal === (splitNumber - 1));
                dogEntries.length = 0;
                dogEntries.push(...distributed);
              }
              // splitTotal === 1 or undefined → unsplit tile, show all dogs
            }
          }

          // Sort dogs by custom order if available, otherwise by armband (EXACT RunOrderTab logic)
          if (dc.dog_run_order && dc.dog_run_order.length > 0) {
            // Deduplicate dog_run_order array (in case grouping created duplicates)
            const uniqueDogRunOrder = [...new Set(dc.dog_run_order)].map((id: any) => id?.toString());
            
            dogEntries.sort((a, b) => {
              // Time-slot shows always run chronologically; a saved order only breaks ties
              // between dogs sharing the same slot.
              if (isTimeslotOrdered) {
                const slotCompare = compareTimeSlots(a.timeSlot, b.timeSlot);
                if (slotCompare !== 0) return slotCompare;
              }

              const aIndex = uniqueDogRunOrder.indexOf(a.scoreId?.toString());
              const bIndex = uniqueDogRunOrder.indexOf(b.scoreId?.toString());
              
              // If both are in saved order, use that order
              if (aIndex !== -1 && bIndex !== -1) {
                return aIndex - bIndex;
              }
              // If only one is in saved order, prioritize it
              if (aIndex !== -1) return -1;
              if (bIndex !== -1) return 1;
              // If neither is in saved order, use the same default sort as Run Order reset/export
              return defaultDogCompare(a, b);
            });

          } else {
            dogEntries.sort(defaultDogCompare);
          }

          // Create display name that includes grouped levels, variation, and sex split
          let displayLevel = dc.level || '';
          const groupedLevelNames = levelsToSearch.filter(level => level !== dc.level);
          if (groupedLevelNames.length > 0) {
            displayLevel = `${dc.level} / ${groupedLevelNames.join(' / ')}`;
          }
          
          // Append variation if present (for AKC Conformation - Open, American Bred, BBE)
          if (classVariation) {
            displayLevel = `${displayLevel} (${classVariation})`;
          }
          
          // Append sex split suffix if present (for AKC Conformation specialty shows)
          if (sexSplit) {
            displayLevel = `${displayLevel} - ${sexSplit}`;
          }

          // Append split label if this class is split
          if (splitNumber !== undefined && splitTotal !== undefined) {
            displayLevel = `${displayLevel} (Split ${splitNumber}/${splitTotal})`;
          }

          const startLetter: string | undefined = (dc as any).start_letter;
          const endLetter: string | undefined = (dc as any).end_letter;
          if (startLetter && endLetter) {
            displayLevel = `${displayLevel} (${startLetter}–${endLetter})`;
          }
          
          // Note: completed_dogs logic removed - now using score-based checked_in status
          const allCompletedDogs: string[] = [];
          
          // Remove duplicates
          const uniqueCompletedDogs = [...new Set(allCompletedDogs)];
          

          
          if (
            dogEntries.length === 0 &&
            splitNumber === undefined &&
            currentRing.running_order.some((otherRo) =>
              otherRo !== runOrderItem &&
              (otherRo.dog_classes || []).some((other) =>
                other !== dc &&
                other.show_element === dc.show_element &&
                other.level === dc.level &&
                ((other as any).section || "") === ((dc as any).section || "") &&
                (other as any).split_number === undefined
              )
            )
          ) {
            continue;
          }

          // Count entries and completed entries
          totalEntries += dogEntries.length;
          completedEntries += uniqueCompletedDogs.length;
          
          // Generate unique ID - include variation, sex_split, and split_number if present
          let classId = `${dc.show_element}-${dc.level}`;
          if (classVariation) {
            classId = `${classId}-${classVariation}`;
          }
          if (sexSplit) {
            classId = `${classId}-${sexSplit}`;
          }
          if (splitNumber !== undefined) {
            classId = `${classId}-split${splitNumber}`;
          }
          if (startLetter && endLetter) {
            classId = `${classId}-${startLetter}${endLetter}`;
          }
          
          processedClasses.push({
            id: classId,
            showElement: dc.show_element || '',
            level: displayLevel,
            section: (dc as any).section, // Include section for C-WAGS Obedience/Rally and UKC Nosework
            judgeName: runOrderItem.judge_name || "",
            dogs: dogEntries,
            originalDogClass: dc,
            completedDogs: uniqueCompletedDogs,
            blind_size: (dc as any).blind_size, // BHA Barn Hunt / ABI URR staging group size
            split_number: splitNumber,
            split_total: splitTotal,
          });
        }
      }
    }
  }


  // IMPORTANT: Do NOT call groupClassesForDisplay here!
  // The data from Judge Schedule Tab is ALREADY correctly grouped.
  // The levelsToSearch logic above (lines 271-278) already handles grouping by including child levels.
  // Calling groupClassesForDisplay would re-group the data and create duplicates.
  
  return {
    processedClasses,
    totalEntries,
    completedEntries
  };
}
